/**
 * 花见小路 - Socket 连接管理
 * 使用 React Context 模式
 */

// 使用默认导入并重命名，避免与局部变量冲突
import { io as socketIO } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import type { ClientToServerEvents, GameState, PlayerId, ServerToClientEvents } from '@hanamikoji/shared';
import { logger } from '../utils/logger';

// Socket 类型定义
export type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
export type SocketMode = 'online' | 'offline-p2p';

export interface SocketRuntimeEnv {
  VITE_SOCKET_URL?: string;
  VITE_API_BASE_URL?: string;
  PROD?: boolean;
}

/**
 * 获取 Socket 服务器地址
 */
function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function readSocketEnv(): SocketRuntimeEnv {
  const env = (import.meta as ImportMeta & { env?: SocketRuntimeEnv }).env;
  return env ?? {};
}

/**
 * Online Socket.IO backend is opt-in. VITE_API_BASE_URL points at the Worker
 * signaling relay HTTP API and must not be reused as a Socket.IO origin.
 */
export function hasExplicitSocketBackend(env: SocketRuntimeEnv = readSocketEnv()): boolean {
  return Boolean(env.VITE_SOCKET_URL?.trim());
}

export function getSocketUrl(env: SocketRuntimeEnv = readSocketEnv()): string | null {
  const envUrl = env.VITE_SOCKET_URL?.trim();
  return envUrl ? normalizeUrl(envUrl) : null;
}

export function shouldInitializeSocketForMode(mode: SocketMode, env: SocketRuntimeEnv = readSocketEnv()): boolean {
  return mode === 'online' && hasExplicitSocketBackend(env);
}

/**
 * 创建 Socket 连接
 * 注意：此函数由 SocketProvider 在用户进入 online 模式后调用，不应在 offline-p2p 默认入口自动调用。
 */
export function createSocket(socketUrl = getSocketUrl()): TypedSocket {
  if (!socketUrl) {
    throw new Error('未配置 VITE_SOCKET_URL，在线服务器模式不可用。');
  }
  logger.info('Initializing Socket.IO connection', { socketUrl });
  
  // 使用 socketIO() 创建连接（避免与变量名 socket 冲突）
  const socket = socketIO(socketUrl, {
    transports: ['websocket', 'polling'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  socket.on('connect', () => {
    logger.info('Socket.IO connected', { socketId: socket.id });
  });

  socket.on('connect_error', (error) => {
    logger.error('Socket.IO connection error', { error });
  });

  socket.on('disconnect', (reason) => {
    logger.warn('Socket.IO disconnected', { reason });
  });

  return socket;
}

/**
 * 派发游戏状态更新事件
 */
export function dispatchGameStateUpdate(state: GameState): void {
  window.dispatchEvent(new CustomEvent('hanamikoji_gameStateUpdate', { detail: state }));
}

/**
 * 派发游戏开始事件
 */
export function dispatchGameStarted(state: GameState, playerId: PlayerId, reconnectToken: string): void {
  window.dispatchEvent(
    new CustomEvent('hanamikoji_gameStarted', { detail: { state, playerId, roomId: state.roomId, reconnectToken } })
  );
}
